import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { List, ListItem } from '@mui/material';

import { useGetDatesQuery } from 'state/apiSlice';
import {
  SavedRouteAlertEvent,
  SavedRouteAlertEventList,
} from 'state/emailAlertsSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { setRmRealtimeOn } from 'state/routeMonitoringSlice';
import { ProjectFeatures } from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectProject,
  setComparison,
  setRmDateTimeRange,
  setRouteSelection,
  setWorkflow,
} from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import { MAX_HOURS, diffInHours } from 'features/common/DateTimeSelector/utils';
import {
  dateTimeToString,
  parseDateTimeIgnoringTz,
} from 'features/common/datetimeUtils';
import { classNames } from 'features/common/utils';
import { findDate } from 'features/task_bar/DatePicker';

import NotificationCard from './NotificationCard';

import styles from './NotificationLog.module.css';

interface INotificationList {
  wide?: boolean;
  notifications: SavedRouteAlertEventList;
}

export function getNotificationRedirectSettings(
  el: SavedRouteAlertEvent,
  is_complete_vnrt: boolean,
) {
  const MIN_VIEW_HOURS = 3;
  const { created_on, resolved, ended_on } = el;

  const createdOn = parseDateTimeIgnoringTz(created_on);
  const endedOn = parseDateTimeIgnoringTz(ended_on);

  const shouldBeRT = !resolved && !is_complete_vnrt;

  if (shouldBeRT) {
    return {
      shouldBeRT,
    };
  }

  const startDate = createdOn.subtract(1, 'hour').startOf('hour');
  const maxEnd = startDate.add(MAX_HOURS, 'hour');

  const endDate = !resolved
    ? maxEnd
    : (() => {
        const resolvedEnd = endedOn.add(1, 'hour').startOf('hour');
        const diff = diffInHours(startDate, resolvedEnd);
        if (diff < MIN_VIEW_HOURS) return startDate.add(MIN_VIEW_HOURS, 'hour');
        if (diff < MAX_HOURS) return resolvedEnd;
        return maxEnd;
      })();

  return {
    shouldBeRT,
    dateTimeRange: {
      start: dateTimeToString(startDate),
      end: dateTimeToString(endDate),
    },
  };
}

export default function NotificationList({
  wide = true,
  notifications,
}: INotificationList) {
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const hasRouteMonitoring = userProject?.features.includes(
    ProjectFeatures.ROUTE_MONITORING,
  );
  const project = useAppSelector(selectProject);
  const { currentData: datesInfo } = useGetDatesQuery(project, {
    skip: !project,
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleClick = (
    _e: MouseEvent<HTMLElement>,
    el: SavedRouteAlertEvent,
  ) => {
    if (!hasRouteMonitoring) return;

    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    const createdOn = parseDateTimeIgnoringTz(el.created_on);
    const day_info = findDate(createdOn.startOf('day'), datesInfo ?? []);
    const settings = getNotificationRedirectSettings(
      el,
      day_info?.is_complete_vnrt ?? false,
    );
    if (!settings) return;

    dispatch(setWorkflow(WorkflowType.RouteMonitoring));
    dispatch(setComparison(el.comparison_month));
    dispatch(setRouteSelection(el.route));
    dispatch(setRmRealtimeOn(settings.shouldBeRT));

    if (settings.dateTimeRange)
      dispatch(setRmDateTimeRange(settings.dateTimeRange));

    navigate('/app', {
      state: { skipInitialSearchHydrate: true },
    });
  };

  return (
    <div
      className={classNames(
        styles.notificationListContainer,
        wide && styles.notificationListFullWidth,
      )}
    >
      <List dense>
        {notifications.map((el) => (
          <ListItem
            key={`${el.hash_id}${wide ? '_wide' : ''}`}
            onClick={(e) => handleClick(e, el)}
          >
            <NotificationCard wide={wide} notification={el} />
          </ListItem>
        ))}
      </List>
    </div>
  );
}
